import type { UseMutationOptions } from "@tanstack/react-query";

import type {
  Platform,
  Prototype,
  PrototypeStatus,
} from "../config/prototype-types";

import { useGet, useGetInfinite, usePut } from "./http";

type PrototypePage = {
  data: Prototype[];
  nextCursor: string | null;
  hasMore: boolean;
};

type UpdateStatusBody = { status: PrototypeStatus };
type UpdateStatusOptions = Omit<
  UseMutationOptions<unknown, Error, UpdateStatusBody>,
  "mutationFn"
>;

export function useUpdatesToday(options?: { enabled?: boolean }) {
  const result = useGet<{ count: number }>(`/api/prototype/updates-today`, {
    enabled: options?.enabled,
  });
  return { ...result, count: result.data?.count };
}

export function useInfinitePrototypes(options?: {
  contributors?: string[];
  platform?: Platform;
  enabled?: boolean;
}) {
  const contributors = options?.contributors ?? [];
  const platform = options?.platform;
  return useGetInfinite<PrototypePage>(
    (pageParam) => {
      const params = new URLSearchParams({ limit: "40" });
      if (pageParam) params.set("cursor", pageParam);
      contributors.forEach((c) => params.append("contributor", c));
      if (platform) params.set("platform", platform);
      return `/api/prototype?${params.toString()}`;
    },
    ["/api/prototype", "infinite", ...contributors, platform ?? "all"],
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      enabled: options?.enabled,
    }
  );
}

export function useUpdatePrototypeStatus(
  prototypeId: string,
  options?: UpdateStatusOptions
) {
  return usePut<UpdateStatusBody>(
    `/api/prototype/${prototypeId}/status`,
    options
  );
}
